import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Users, Search, Plus, Trash2, Edit2, Building2, MapPin, Briefcase, Mail, Phone, RefreshCw } from "lucide-react";
import { api } from "../api.js";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { Badge } from "../components/ui/badge.js";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../components/ui/table.js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog.js";
import { formatRut, validarRut } from "../../lib/rut.js";
import type { ClienteFrecuenteItem } from "../components/ClienteSelectorModal.js";

export default function ClientesPage() {
  const [clientes, setClientes] = useState<ClienteFrecuenteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmisorFilter, setSelectedEmisorFilter] = useState<string>("ALL");

  // Modal crear/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    rutEmisor: "",
    rut: "",
    razonSocial: "",
    giro: "",
    direccion: "",
    comuna: "",
    ciudad: "Santiago",
    email: "",
    telefono: "",
    glosaHabitual: "",
  });
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    rutEmisor?: string;
    rut?: string;
    razonSocial?: string;
  }>({});

  const handleRutEmisorChange = (val: string) => {
    const filtered = val.replace(/[^0-9kK.-]/g, "");
    setFormData((prev) => ({ ...prev, rutEmisor: filtered }));
    if (formErrors.rutEmisor) setFormErrors((prev) => ({ ...prev, rutEmisor: undefined }));
  };

  const handleRutEmisorBlur = () => {
    if (!formData.rutEmisor.trim()) return;
    const formatted = formatRut(formData.rutEmisor.trim());
    setFormData((prev) => ({ ...prev, rutEmisor: formatted }));
    if (!validarRut(formatted)) {
      setFormErrors((prev) => ({ ...prev, rutEmisor: "RUT emisor inválido (revisa el dígito verificador)" }));
    } else {
      setFormErrors((prev) => ({ ...prev, rutEmisor: undefined }));
    }
  };

  const handleRutClienteChange = (val: string) => {
    const filtered = val.replace(/[^0-9kK.-]/g, "");
    setFormData((prev) => ({ ...prev, rut: filtered }));
    if (formErrors.rut) setFormErrors((prev) => ({ ...prev, rut: undefined }));
  };

  const handleRutClienteBlur = () => {
    if (!formData.rut.trim()) return;
    const formatted = formatRut(formData.rut.trim());
    setFormData((prev) => ({ ...prev, rut: formatted }));
    if (!validarRut(formatted)) {
      setFormErrors((prev) => ({ ...prev, rut: "RUT cliente inválido (revisa el dígito verificador)" }));
    } else {
      setFormErrors((prev) => ({ ...prev, rut: undefined }));
    }
  };

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api<{ clientes: ClienteFrecuenteItem[] }>("/clientes");
      if (Array.isArray(res.clientes)) {
        setClientes(res.clientes);
      }
    } catch (err) {
      toast.error("Error al cargar clientes frecuentes: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  // Lista de emisores únicos disponibles en los clientes
  const distinctEmisores = useMemo(() => {
    const set = new Set<string>();
    clientes.forEach((c) => {
      if (c.rutEmisor) set.add(c.rutEmisor);
    });
    return Array.from(set);
  }, [clientes]);

  // Filtrado reactivo
  const filteredClientes = useMemo(() => {
    let pool = clientes;
    if (selectedEmisorFilter !== "ALL") {
      pool = pool.filter((c) => c.rutEmisor === selectedEmisorFilter);
    }
    if (!searchTerm.trim()) return pool;

    const term = searchTerm.toLowerCase().trim();
    const cleanTermRut = term.replace(/[.\s-]/g, "");

    return pool.filter((c) => {
      const rutClean = c.rut.replace(/[.\s-]/g, "").toLowerCase();
      const razon = (c.razonSocial ?? "").toLowerCase();
      const giro = (c.giro ?? "").toLowerCase();
      const comuna = (c.comuna ?? "").toLowerCase();
      return (
        rutClean.includes(cleanTermRut) ||
        razon.includes(term) ||
        giro.includes(term) ||
        comuna.includes(term)
      );
    });
  }, [clientes, selectedEmisorFilter, searchTerm]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormErrors({});
    setFormData({
      rutEmisor: selectedEmisorFilter !== "ALL" ? selectedEmisorFilter : distinctEmisores[0] || "",
      rut: "",
      razonSocial: "",
      giro: "",
      direccion: "",
      comuna: "",
      ciudad: "Santiago",
      email: "",
      telefono: "",
      glosaHabitual: "",
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (c: ClienteFrecuenteItem) => {
    setEditingId(c.id);
    setFormErrors({});
    setFormData({
      rutEmisor: c.rutEmisor,
      rut: formatRut(c.rut),
      razonSocial: c.razonSocial,
      giro: c.giro ?? "",
      direccion: c.direccion ?? "",
      comuna: c.comuna ?? "",
      ciudad: c.ciudad ?? "Santiago",
      email: c.email ?? "",
      telefono: c.telefono ?? "",
      glosaHabitual: c.glosaHabitual ?? "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number, razon: string) => {
    if (!confirm(`¿Eliminar al cliente "${razon}" de tu libreta frecuente?`)) return;
    try {
      await api(`/clientes/${id}`, { method: "DELETE" });
      toast.success("Cliente eliminado");
      setClientes((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      toast.error("No se pudo eliminar el cliente: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: { rutEmisor?: string; rut?: string; razonSocial?: string } = {};

    const formattedEmisor = formatRut(formData.rutEmisor.trim());
    const formattedCliente = formatRut(formData.rut.trim());
    const cleanRazon = formData.razonSocial.trim();

    if (!formData.rutEmisor.trim()) {
      errors.rutEmisor = "Debes ingresar el RUT del emisor";
    } else if (!validarRut(formattedEmisor)) {
      errors.rutEmisor = "RUT emisor inválido (revisa el dígito verificador)";
    }

    if (!formData.rut.trim()) {
      errors.rut = "Debes ingresar el RUT del cliente";
    } else if (!validarRut(formattedCliente)) {
      errors.rut = "RUT cliente inválido (revisa el dígito verificador)";
    }

    if (!cleanRazon) {
      errors.razonSocial = "La Razón Social o Nombre es obligatoria";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Por favor completa o corrige los campos destacados en rojo");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // Actualizar
        const res = await api<{ success: boolean; cliente: ClienteFrecuenteItem }>(`/clientes/${editingId}`, {
          method: "PUT",
          body: JSON.stringify({
            razonSocial: cleanRazon,
            giro: formData.giro.trim() || null,
            direccion: formData.direccion.trim() || null,
            comuna: formData.comuna.trim() || null,
            ciudad: formData.ciudad.trim() || "Santiago",
            email: formData.email.trim() || null,
            telefono: formData.telefono.trim() || null,
            glosaHabitual: formData.glosaHabitual.trim() || null,
          }),
        });
        toast.success("Cliente actualizado");
        setClientes((prev) => prev.map((c) => (c.id === editingId ? res.cliente : c)));
      } else {
        // Crear
        const res = await api<{ success: boolean; cliente: ClienteFrecuenteItem }>("/clientes", {
          method: "POST",
          body: JSON.stringify({
            ...formData,
            rutEmisor: formattedEmisor,
            rut: formattedCliente,
            razonSocial: cleanRazon,
          }),
        });
        toast.success("Cliente guardado en el directorio");
        setClientes((prev) => [res.cliente, ...prev.filter((c) => c.id !== res.cliente.id)]);
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error("Error al guardar: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Directorio de Clientes
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Libreta de clientes frecuentes para autocompletado instantáneo en Factura, BHE y Boleta Electrónica.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchClientes} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <Button size="sm" onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-64 sm:w-80">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar por RUT, Razón Social, Giro..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-md border border-input bg-background pl-9 pr-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                />
              </div>

              {distinctEmisores.length > 1 && (
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-muted-foreground">Emisor:</span>
                  <select
                    value={selectedEmisorFilter}
                    onChange={(e) => setSelectedEmisorFilter(e.target.value)}
                    className="rounded-md border border-input bg-background px-2.5 py-1.5 text-xs font-mono"
                  >
                    <option value="ALL">Todos los emisores ({clientes.length})</option>
                    {distinctEmisores.map((emisor) => (
                      <option key={emisor} value={emisor}>
                        {formatRut(emisor)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="text-xs text-muted-foreground">
              Mostrando <strong>{filteredClientes.length}</strong> de {clientes.length} cliente(s)
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading && clientes.length === 0 ? (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              Cargando clientes...
            </div>
          ) : filteredClientes.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <Building2 className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-semibold text-foreground">No hay clientes frecuentes registrados</p>
              <p className="text-xs text-muted-foreground max-w-sm mt-1">
                {searchTerm
                  ? "No hay resultados para tu búsqueda."
                  : "Los clientes se aprenden automáticamente cuando emites facturas o boletas, o puedes agregar uno manualmente."}
              </p>
              {!searchTerm && (
                <Button size="sm" onClick={handleOpenCreate} className="mt-4">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Agregar primer cliente
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-border/80 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-32">Emisor</TableHead>
                    <TableHead className="w-36">RUT Cliente</TableHead>
                    <TableHead className="min-w-[180px]">Razón Social / Nombre</TableHead>
                    <TableHead className="min-w-[150px]">Giro</TableHead>
                    <TableHead className="min-w-[160px]">Dirección y Comuna</TableHead>
                    <TableHead className="min-w-[140px]">Glosa Habitual</TableHead>
                    <TableHead className="w-24 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClientes.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-[11px] py-0">
                          {formatRut(c.rutEmisor)}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono font-medium text-xs">
                        {formatRut(c.rut)}
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {c.razonSocial}
                        {c.email && (
                          <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                            <Mail className="h-3 w-3" />
                            {c.email}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {c.giro || <span className="text-muted-foreground/40 italic">Sin giro</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {[c.direccion, c.comuna].filter(Boolean).join(", ") || (
                          <span className="text-muted-foreground/40 italic">Sin dirección</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {c.glosaHabitual || <span className="text-muted-foreground/40 italic">-</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => handleOpenEdit(c)}
                            title="Editar cliente"
                          >
                            <Edit2 className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDelete(c.id, c.razonSocial)}
                            title="Eliminar cliente"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear / Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <form onSubmit={handleSave}>
            <DialogHeader>
              <DialogTitle>{editingId ? "Editar Cliente" : "Nuevo Cliente Frecuente"}</DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Actualiza los datos del cliente para futuras emisiones."
                  : "Registra un cliente asociado a un emisor específico."}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 py-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">RUT Emisor *</label>
                  <input
                    type="text"
                    placeholder="76.123.456-7"
                    value={formData.rutEmisor}
                    onChange={(e) => handleRutEmisorChange(e.target.value)}
                    onBlur={handleRutEmisorBlur}
                    required
                    disabled={!!editingId}
                    className={`w-full rounded-md border bg-background px-3 py-1.5 text-xs font-mono transition-colors ${
                      formErrors.rutEmisor ? "border-destructive focus-visible:ring-destructive" : "border-input"
                    }`}
                  />
                  {formErrors.rutEmisor && (
                    <p className="text-[11px] text-destructive font-medium mt-0.5">{formErrors.rutEmisor}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">RUT Cliente (Receptor) *</label>
                  <input
                    type="text"
                    placeholder="12.345.678-9"
                    value={formData.rut}
                    onChange={(e) => handleRutClienteChange(e.target.value)}
                    onBlur={handleRutClienteBlur}
                    required
                    disabled={!!editingId}
                    className={`w-full rounded-md border bg-background px-3 py-1.5 text-xs font-mono transition-colors ${
                      formErrors.rut ? "border-destructive focus-visible:ring-destructive" : "border-input"
                    }`}
                  />
                  {formErrors.rut && (
                    <p className="text-[11px] text-destructive font-medium mt-0.5">{formErrors.rut}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">Razón Social o Nombre Completo *</label>
                <input
                  type="text"
                  placeholder="Empresa o Persona SpA"
                  value={formData.razonSocial}
                  onChange={(e) => {
                    setFormData({ ...formData, razonSocial: e.target.value });
                    if (formErrors.razonSocial) setFormErrors((prev) => ({ ...prev, razonSocial: undefined }));
                  }}
                  required
                  className={`w-full rounded-md border bg-background px-3 py-1.5 text-sm transition-colors ${
                    formErrors.razonSocial ? "border-destructive focus-visible:ring-destructive" : "border-input"
                  }`}
                />
                {formErrors.razonSocial && (
                  <p className="text-[11px] text-destructive font-medium mt-0.5">{formErrors.razonSocial}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">Giro Comercial (Factura)</label>
                <input
                  type="text"
                  placeholder="Servicios informáticos / Consultoría"
                  value={formData.giro}
                  onChange={(e) => setFormData({ ...formData, giro: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">Dirección</label>
                  <input
                    type="text"
                    placeholder="Av. Providencia 1234, Of 501"
                    value={formData.direccion}
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">Comuna</label>
                  <input
                    type="text"
                    placeholder="Providencia"
                    value={formData.comuna}
                    onChange={(e) => setFormData({ ...formData, comuna: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">Correo (PDF automático)</label>
                  <input
                    type="email"
                    placeholder="facturacion@empresa.cl"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">Teléfono</label>
                  <input
                    type="text"
                    placeholder="+56912345678"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">Glosa / Detalle Habitual (opcional)</label>
                <input
                  type="text"
                  placeholder="Servicios profesionales mensuales"
                  value={formData.glosaHabitual}
                  onChange={(e) => setFormData({ ...formData, glosaHabitual: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Guardando..." : "Guardar Cliente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
